import { useContext, useEffect, useState } from 'react';

import { faCheckCircle } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { differenceInMilliseconds } from 'date-fns';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from './constants';
import type { TrialStatusData } from './types';
import { IframeMessageType, ModalType, TrialStatus } from './types';

const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;
const MILLISECONDS_IN_AN_HOUR = 1000 * 60 * 60;
const MILLISECONDS_IN_A_MINUTE = 1000 * 60;

export const getTimeRemaining = (endsOn?: string) => {
  if (!endsOn) return { days: 0, hours: 0, minutes: 0 };

  const diff = differenceInMilliseconds(new Date(endsOn), new Date());

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };

  const days = Math.floor(diff / MILLISECONDS_IN_A_DAY);
  const hours = Math.floor((diff / MILLISECONDS_IN_AN_HOUR) % 24);
  const minutes = Math.floor((diff / MILLISECONDS_IN_A_MINUTE) % 60);

  return { days, hours, minutes };
};

const CountdownUnit = ({ value, unit }: { value: number; unit: string }) => (
  <div className="vai-flex vai-flex-col vai-items-center">
    <p className="vai-font-inter vai-font-bold vai-text-sm [@media(max-width:310px)]:vai-text-[12px]">
      {value}
    </p>
    <p className="vai-font-inter vai-text-xs">{unit}</p>
  </div>
);

interface Props {
  trialStatusData?: TrialStatusData;
}

export const FreeTrialBanner = ({ trialStatusData }: Props) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);

  const isFreeTrial = trialStatusData?.status === TrialStatus.TRIAL;
  const isCentralizedBilling = trialStatusData?.centralizedBilling;
  const showUpgradeRequestSuccess =
    isCentralizedBilling &&
    trialStatusData?.status === TrialStatus.PAID_BUT_PROVISIONAL;
  const showUpgradeButton = trialStatusData?.status === TrialStatus.TRIAL;
  const countdownEndsOn = isFreeTrial
    ? trialStatusData?.trialEndsOn
    : trialStatusData?.paidEndsOn;

  const [{ days, hours, minutes }, setTimeLeft] = useState(() =>
    getTimeRemaining(countdownEndsOn),
  );

  useEffect(() => {
    // Update countdown every minute
    const interval = setInterval(() => {
      setTimeLeft(getTimeRemaining(countdownEndsOn));
    }, MILLISECONDS_IN_A_MINUTE);

    return () => clearInterval(interval);
  }, [countdownEndsOn]);

  return (
    <div className="vai-relative vai-bg-[rgb(33,48,147)] vai-p-2 vai-text-white vai-flex vai-items-center vai-justify-between">
      <img
        alt="Branded shape pattern"
        className="vai-absolute vai-bottom-0 vai-right-0 vai-h-full"
        src="/assets/images/external/branded-free-trial.svg"
      />

      <p className="[@media(max-width:310px)]:vai-text-sm vai-font-inter vai-font-bold vai-z-10">
        <Trans>AI Trial</Trans>
      </p>

      {countdownEndsOn && (
        <div className="vai-flex vai-items-center vai-gap-2 [@media(max-width:320px)]:vai-gap-1 vai-z-10">
          <CountdownUnit value={days} unit={t`Days`} />
          <div className="vai-h-8 vai-w-[1px] vai-bg-[rgba(0,0,0,.7)]" />
          <CountdownUnit value={hours} unit={t`Hours`} />
          <div className="vai-h-8 vai-w-[1px] vai-bg-[rgba(0,0,0,.7)]" />
          <CountdownUnit value={minutes} unit={t`Min`} />
        </div>
      )}

      {showUpgradeRequestSuccess && (
        <div className="vai-rounded-full vai-bg-green-100 vai-bg-opacity-90 vai-px-2 vai-py-1 vai-flex vai-items-center vai-gap-1 vai-z-10">
          <FontAwesomeIcon
            className="vai-text-green-600 vai-w-3 vai-h-3"
            icon={faCheckCircle}
          />
          <p className="vai-font-inter vai-text-zinc-950 vai-text-[12px] [@media(max-width:310px)]:vai-text-xs vai-font-medium">
            <Trans>Upgrade Requested</Trans>
          </p>
        </div>
      )}

      {showUpgradeButton && (
        <Button
          className="!vai-bg-green-700 hover:!vai-bg-green-800 vai-h-6 !vai-px-2 !vai-text-[12px] vai-z-10"
          onClick={() => {
            trackEvent(mixpanelEvents.externalUpgradeModalOpen, {
              ...externalPatientContext,
              source: 'FREE_TRIAL_BANNER',
            });
            window.parent.postMessage(
              {
                type: IframeMessageType.MODAL_OPEN,
                payload: {
                  type: ModalType.UPGRADE,
                  patientId: externalPatientContext.patientId,
                  imageId: externalPatientContext.imageId,
                  externalPatientId: externalPatientContext.externalPatientId,
                  externalImageId: externalPatientContext.externalImageId,
                },
              },
              '*',
            );
          }}
        >
          {isCentralizedBilling ? (
            <Trans>Request Upgrade</Trans>
          ) : (
            <Trans>Upgrade Now</Trans>
          )}
        </Button>
      )}
    </div>
  );
};
